import React, { FC } from 'react'

import { useMutation } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { FixContractsContractVersionDocument } from 'generated/resources/graphql'
import { IObservableArray } from 'mobx'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import { ValidationError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { employeeUser } from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'
import type { FlashType } from 'modules/core/store/models/app'

import { useFetchContractsPermissions } from 'modules/contracts/hooks/useFetchContractsPermissions'
import { useFetchCustomFields } from 'modules/contracts/hooks/useFetchCustomFields'
import { useNewAdditionalCompensationsFeatureEnabled } from 'modules/contracts/hooks/useNewAdditionalCompensationsFeatureEnabled'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getCustomFieldKey } from 'modules/contracts/lib/customFields'
import { isGraphQLType } from 'modules/contracts/lib/types'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { getFixFlowForm } from './lib/fixFlowForm'

interface FixFlowContextProps {
  form: Form
  onSubmit: (onSuccess: () => void) => void
  canEdit: boolean
  isSubmitting: boolean
  loadingPermissions: boolean
  loadingCustomFields: boolean
  customValues: IObservableArray<CustomFieldValue>
  customFields: IObservableArray<ResourceField>
}

interface FixFlowProviderProps {
  fieldIds?: number[]
  children: React.ReactNode
}

const FixFlowProviderContext = React.createContext<FixFlowContextProps | null>(null)

export const FixFlowProvider: FC<FixFlowProviderProps> = observer(({ fieldIds, children }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const {
    employee,
    compensations,
    additionalCompensations,
    contractVersion,
    setCustomFields,
    setCustomValues,
    contractVersionPolicy,
  } = useContractFlow()
  const canEdit = policyAuthorizations.for('contracts.edit', employee)
  const { loading: loadingPermissions } = useFetchContractsPermissions(employee)
  const {
    loading: loadingCustomFields,
    resourceFields,
    valuesCollection,
  } = useFetchCustomFields({
    fieldableId: employeeLegalEntityId(employee),
    contractVersion,
    employee,
  })

  const { company } = useCurrentSession()
  const isNewAdditionalCompensationsSupportEnabled = useNewAdditionalCompensationsFeatureEnabled({
    companyId: company.id,
  })
  const [fixContractsContractVersion] = useMutation(FixContractsContractVersionDocument)

  const form = useUnsafeMemo(() => {
    return contractVersion
      ? getFixFlowForm(
          contractVersion,
          employee,
          contractVersionPolicy,
          resourceFields,
          valuesCollection,
          policyAuthorizations
        )
      : null
  }, [employee, loadingCustomFields, valuesCollection, contractVersion, loadingPermissions]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loadingCustomFields) {
      setCustomFields(
        fieldIds
          ? resourceFields.filter((field) => fieldIds?.includes(field.attributes.field_id))
          : resourceFields
      )
      setCustomValues(valuesCollection)
    }
  }, [loadingCustomFields]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loadingCustomFields || loadingPermissions) {
    return (
      <Box width="full" height="full" justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  if (!contractVersion || !form || !employee) return null

  const getEditedCustomFields = (customFields: ResourceField[]) => {
    const editedCustomFields: { [key: string]: string }[] = []

    return customFields.reduce((editedCustomFields, field) => {
      const key = getCustomFieldKey(field)
      const cf_field = form.get(key)
      if (cf_field.isDirty) {
        const attributes = field.attributes
        editedCustomFields.push({
          identifier: attributes.identifier,
          value: cf_field._mapOut(),
        })
      }
      return editedCustomFields
    }, editedCustomFields)
  }

  const showFlashMessage = (type: FlashType, message: string): void => {
    app.setFlash({
      type,
      message,
    })
  }

  const onSubmit = (onSuccess: () => void) => {
    setIsSubmitting(true)
    const api = ApiClient()
    form.get('payroll_policy_id').set(contractVersionPolicy?.attributes.payroll_policy_id)
    const data = form.serialized
    delete data['es_tariff_group_name']
    data.employee_id = employee.id
    data.additional_compensations = compensations?.map((compensation) => compensation.attributes)
    data.custom_fields = getEditedCustomFields(resourceFields)

    if (!data.has_trial_period) data.trial_period_ends_on = null

    setIsSubmitting(true)
    if (isNewAdditionalCompensationsSupportEnabled) {
      return fixContractsContractVersion({
        variables: {
          additionalCompensations: data.additional_compensations,
          newAdditionalCompensations: additionalCompensations?.map(({ tmpID: _, ...rest }) => {
            return {
              id: rest.id,
              amount: rest.amountStrategy?.amount,
              amountStrategyType: rest.amountStrategyType,
              contractVersionId: rest.contractVersionId,
              description: rest.description,
              firstPaymentOn: rest.firstPaymentOn,
              payrollConceptId: rest.payrollConceptId,
              recurrence: rest.recurrence,
              upperLimit:
                rest.amountStrategy &&
                isGraphQLType(rest.amountStrategy, 'ContractsVariableAmountStrategy')
                  ? rest.amountStrategy?.upperLimit
                  : 0,
            }
          }),
          customFields: data.custom_fields,
          employeeId: employee.id,
          id: contractVersion?.attributes.id,
          contractsEsTariffGroupId: data.contracts_es_tariff_group_id,
          deActivityType: data.de_activity_type,
          deContractTypeId: data.de_contract_type_id,
          deEmploymentType: data.de_employment_type,
          deFlatRateTax: data.de_flat_rate_tax,
          dePersonalKeyGroupId: data.de_personal_key_group_id,
          effectiveOn: data.effective_on,
          endsOn: data.ends_on,
          esA3innuvaJobPositionId: data.es_a3innuva_job_position_id,
          esA3innuvaJobPositionPayrollIntegrationCode:
            data.es_a3innuva_job_position_payroll_integration_code,
          esA3innuvaJobPositionPayrollIntegrationName:
            data.es_a3innuva_job_position_payroll_integration_name,
          esAgreementCodePayrollIntegrationCode: data.es_agreement_code_payroll_integration_code,
          esAgreementCodePayrollIntegrationName: data.es_agreement_code_payroll_integration_name,
          esClassificationId: data.es_classification_id,
          esClassificationPayrollIntegrationCode: data.es_classification_payroll_integration_code,
          esClassificationPayrollIntegrationName: data.es_classification_payroll_integration_name,
          esCnoOccupationId: data.es_cno_occupation_id,
          esCnoOccupationPayrollIntegrationCode: data.es_cno_occupation_payroll_integration_code,
          esCnoOccupationPayrollIntegrationName: data.es_cno_occupation_payroll_integration_name,
          esContractObservations: data.es_contract_observations,
          esContractTypeId: data.es_contract_type_id,
          esContractTypePayrollIntegrationCode: data.es_contract_type_payroll_integration_code,
          esContractTypePayrollIntegrationName: data.es_contract_type_payroll_integration_name,
          esContributionTypeId: data.es_contribution_type_id,
          esContributionTypePayrollIntegrationCode:
            data.es_contribution_type_payroll_integration_code,
          esContributionTypePayrollIntegrationName:
            data.es_contribution_type_payroll_integration_name,
          esCotizationGroup: data.es_cotization_group,
          esEducationLevelId: data.es_education_level_id,
          esEducationLevelPayrollIntegrationCode: data.es_education_level_payroll_integration_code,
          esEducationLevelPayrollIntegrationName: data.es_education_level_payroll_integration_name,
          esHasTeleworkingContract: data.es_has_teleworking_contract,
          esJobDescription: data.es_job_description,
          esOccupationCodeId: data.es_occupation_code_id,
          esOccupationCodePayrollIntegrationCode: data.es_occupation_code_payroll_integration_code,
          esOccupationCodePayrollIntegrationName: data.es_occupation_code_payroll_integration_name,
          esProfessionalCategoryId: data.es_professional_category_id,
          esRegimeId: data.es_regime_id,
          esRegimePayrollIntegrationCode: data.es_regime_payroll_integration_code,
          esRegimePayrollIntegrationName: data.es_regime_payroll_integration_name,
          esTariffGroupId: data.es_tariff_group_id ? String(data.es_tariff_group_id) : '',
          esWorkingDayTypeId: data.es_working_day_type_id,
          frCoefficient: data.fr_coefficient,
          frContractTypeId: data.fr_contract_type_id,
          frEmployeeType: data.fr_employee_type,
          frForfaitJours: data.fr_forfait_jours,
          frJoursParAn: data.fr_jours_par_an,
          frJoursParAnCents: data.fr_jours_par_an_cents,
          frLevelId: data.fr_level_id,
          frMutualId: data.fr_mutual_id,
          frProfessionalCategoryId: data.fr_professional_category_id,
          frStepId: data.fr_step_id,
          frWorkTypeId: data.fr_work_type_id,
          hasTrialPeriod: data.has_trial_period,
          jobCatalogLevelId: data.job_catalog_level_id,
          // TODO: Add the job catalog tree node uuid to the mutation DTO
          // once we have it in the contract version
          jobTitle: data.job_title,
          maxLegalYearlyHours: data.max_legal_yearly_hours,
          maximumWeeklyHours: data.maximum_weekly_hours,
          minRestMinutesBetweenDays: data.min_rest_minutes_between_days,
          maxWorkMinutesPerDay: data.max_work_minutes_per_day,
          ptContractTypeId: data.pt_contract_type_id,
          salaryAmount: data.salary_amount,
          salaryFrequency: data.salary_frequency,
          startsOn: data.starts_on,
          trialPeriodEndsOn: data.trial_period_ends_on,
          workingHours: data.working_hours,
          workingHoursFrequency: data.working_hours_frequency,
          workingTimePercentageInCents: data.working_time_percentage_in_cents,
          bankHolidayTreatment: data.bank_holiday_treatment,
          annualWorkingTimeDistribution: data.annual_working_time_distribution,
        },
        onCompleted: () => {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.fix.confirmation', {
              employeeFullName: employeeUser(employee).fullName(),
            })
          )
          form.cleanAll()
          setIsSubmitting(false)
          onSuccess()
        },
        onError: (error) => {
          showFlashMessage('wrong', error.message)
          form.cleanAll()
          setIsSubmitting(false)
        },
      })
    } else {
      api
        .post(`/api/rest/contracts/contract_versions/fix`, data)
        .then(() => {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.fix.confirmation', {
              employeeFullName: employeeUser(employee).fullName(),
            })
          )
          onSuccess()
        })
        .catch((e) => {
          if (e instanceof ValidationError) showFlashMessage('wrong', e.message)
          else showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
        })
        .finally(() => {
          form.cleanAll()
          setIsSubmitting(false)
        })
    }
  }

  return (
    <FixFlowProviderContext.Provider
      value={{
        form,
        canEdit,
        onSubmit,
        isSubmitting,
        loadingPermissions,
        loadingCustomFields,
        customFields: resourceFields,
        customValues: valuesCollection,
      }}
    >
      {children}
    </FixFlowProviderContext.Provider>
  )
})

export const useFixFlowProvider = (): FixFlowContextProps => {
  const fixFlow = React.useContext(FixFlowProviderContext)

  if (!fixFlow) {
    throw new Error('useFixFlowProvider must be used within a FixFlowProvider')
  }

  return fixFlow
}
